import React, { ReactNode, useEffect, useRef, useState } from "react";
import styles from "./proctoringOptionsGrid.module.css";
import { CoreAccordion, CorePaper } from "../../core";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper } from "../../wrapper";
import { Application, Extension, ProctoringOption, ProctoringPlatforms } from "@verity/proctoring-option/types";
import { PROCTORING_OPTION_TEXT, ProctoringOptionCategory } from "./option-text";
import { Link } from "@mui/material";
import type { VerityApi } from "../../api/fetch";
import DisableFeaturesOptions from "./option-sections/disable-features-options";
import OtherActivitiesOptions from "./option-sections/other-activities-options";
import RecordOptions from "./option-sections/record-options";
import BlockItemsOptions from "./option-sections/restrict-applications-options";
import ShowToolsOptions from "./option-sections/show-tools-options";
import TabsOptions from "./option-sections/tab-options";
import VerifyIdentityOptions from "./option-sections/verify-identity-options";
import i18nPackage from "../../i18n/i18n";
export type ProctoringOptionsListProps<T extends Partial<ProctoringOption>> = {
	option: T;
	updateOption: (options: T) => void;
	disabled?: boolean;
	shouldShowOption: (option: keyof T) => boolean;
	isOptionAllowed: <O extends keyof T>(option: O, value?: T[O]) => boolean;
	getPartiallySupportedPlatforms: <O extends keyof T>(option: O, value?: T[O]) => Set<keyof ProctoringPlatforms>;
	applications: Application[];
	extensions: Extension[];
	allowedCategories: ProctoringOptionCategory[];
	hasPassword?: boolean;
	horizontalOrientation?: boolean;
	setBodyRef?: (el: HTMLDivElement | null) => void;
};

const RenderOptions = ({
	optionsKey,
	useAccordion,
	expanded,
	categoryTitle,
	handleExpandedChange,
	children,
}: {
	optionsKey: ProctoringOptionCategory;
	useAccordion?: boolean;
	expanded: boolean;
	categoryTitle: ReactNode;
	handleExpandedChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
	children: ReactNode;
}) =>
	useAccordion ? (
		<CoreAccordion
			summary={<div className={styles.categoryTitle}>{categoryTitle}</div>}
			expanded={expanded}
			handleExpandedChange={handleExpandedChange}
			sx={{ borderRadius: "7px", boxShadow: "0 0 5px 0 rgb(0 0 0/20%)" }}
			disableGutters
		>
			{children}
		</CoreAccordion>
	) : (
		<div>
			<div className={styles.categoryTitle}>{optionsKey}</div>
			<div className={styles.divider} />
			{children}
		</div>
	);

export function ProctoringOptionsList<T extends Partial<ProctoringOption>>({
	option,
	updateOption,
	disabled,
	shouldShowOption,
	isOptionAllowed,
	getPartiallySupportedPlatforms,
	applications,
	fetchChromeExtensionData,
	extensions,
	allowedCategories,
	hasPassword,
	horizontalOrientation,
	setBodyRef,
	instituteUuid,
	getInstituteSettings,
	courseId,
}: ProctoringOptionsListProps<T> & {
	instituteUuid?: string;
	getInstituteSettings?: VerityApi["getInstituteSettings"];
	fetchChromeExtensionData: (id: string) => Promise<Extension | null>;
	courseId?: number;
}) {
	const isMobileLayout = useMobileLayout();

	const optionBodyRef = useRef<HTMLDivElement>(null); // for categories in configuration step, so after next is clicked, focus goes to body
	useEffect(() => {
		if (setBodyRef) setBodyRef(optionBodyRef.current);
	}, [setBodyRef]);

	const [expandedCategory, setExpandedCategory] = useState<ProctoringOptionCategory | undefined>(undefined);
	const handleExpandedChange =
		(category: ProctoringOptionCategory) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
			setExpandedCategory(isExpanded ? category : undefined);
		};
	const getCategoryTitle = (categoryName: ProctoringOptionCategory) => {
		const categoryNumber = allowedCategories.findIndex((category) => category === categoryName) + 1;
		return (
			<span>
				{categoryNumber}. {categoryName}
			</span>
		);
	};

	return (
		<div className={`${styles.listWrapper} ${isMobileLayout ? styles.listWrapperCompact : ""}`}>
			<div className={isMobileLayout ? styles.optionList3 : styles.optionList2}>
				<ConditionalWrapper
					condition={isMobileLayout}
					whenTrue={(children) => (
						<CorePaper component="section" className={styles.optionPaper} sx={{ padding: "10px" }}>
							{children}
						</CorePaper>
					)}
				>
					<div
						className={`
							${styles.proctorSettingsListDescriptionText}
							${isMobileLayout ? styles.proctorSettingsListDescriptionTextCompact : ""}
						`}
					>
						{Object.entries(PROCTORING_OPTION_TEXT)
							.filter(([, { settingDescription }]) => settingDescription)
							.every(([o]) => !isOptionAllowed(o as keyof ProctoringOption)) ? (
							<div className={styles.errorMessage}>
								{i18nPackage.t("editQuizPage.configurationStep.allOptionsDisabled")}
							</div>
						) : (
							<div className={styles.proctorSettingsGridPlatformsText}>
								{i18nPackage.t("editQuizPage.configurationStep.selectWantedOptions")}
								<Link
									href="https://support.yuja.com/hc/en-us/articles/18378524033559-Overview-of-YuJa-Verity-Proctor-Configurations"
									target="_blank"
									rel="noreferrer"
									sx={{ marginLeft: "3px" }}
								>
									{i18nPackage.t("editQuizPage.configurationStep.learnMore")}
								</Link>
								.
							</div>
						)}
					</div>
				</ConditionalWrapper>
				{allowedCategories.includes("Record") && (
					<RenderOptions
						optionsKey="Record"
						useAccordion={isMobileLayout}
						expanded={expandedCategory === "Record"}
						categoryTitle={getCategoryTitle("Record")}
						handleExpandedChange={handleExpandedChange("Record")}
					>
						<RecordOptions
							option={option}
							updateOption={updateOption}
							disabled={disabled}
							shouldShowOption={shouldShowOption}
							isOptionAllowed={isOptionAllowed}
							getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
							horizontalOrientation={horizontalOrientation}
							instituteUuid={instituteUuid}
							getInstituteSettings={getInstituteSettings}
						/>
					</RenderOptions>
				)}
				{allowedCategories.includes("Verify Identity") && (
					<RenderOptions
						optionsKey="Verify Identity"
						useAccordion={isMobileLayout}
						expanded={expandedCategory === "Verify Identity"}
						categoryTitle={getCategoryTitle("Verify Identity")}
						handleExpandedChange={handleExpandedChange("Verify Identity")}
					>
						<VerifyIdentityOptions
							option={option}
							updateOption={updateOption}
							disabled={disabled}
							shouldShowOption={shouldShowOption}
							isOptionAllowed={isOptionAllowed}
							horizontalOrientation={horizontalOrientation}
							getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
						/>
					</RenderOptions>
				)}
				{allowedCategories.includes("Tabs") && (
					<RenderOptions
						optionsKey="Tabs"
						useAccordion={isMobileLayout}
						expanded={expandedCategory === "Tabs"}
						categoryTitle={getCategoryTitle("Tabs")}
						handleExpandedChange={handleExpandedChange("Tabs")}
					>
						<TabsOptions
							option={option}
							updateOption={updateOption}
							disabled={disabled}
							shouldShowOption={shouldShowOption}
							isOptionAllowed={isOptionAllowed}
							horizontalOrientation={horizontalOrientation}
							getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
						/>
					</RenderOptions>
				)}
				{allowedCategories.includes("Disable Features") && (
					<RenderOptions
						optionsKey="Disable Features"
						useAccordion={isMobileLayout}
						expanded={expandedCategory === "Disable Features"}
						categoryTitle={getCategoryTitle("Disable Features")}
						handleExpandedChange={handleExpandedChange("Disable Features")}
					>
						<DisableFeaturesOptions
							option={option}
							updateOption={updateOption}
							disabled={disabled}
							shouldShowOption={shouldShowOption}
							isOptionAllowed={isOptionAllowed}
							hasPassword={hasPassword}
							extensions={extensions}
							fetchChromeExtensionData={fetchChromeExtensionData}
							horizontalOrientation={horizontalOrientation}
							getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
						/>
					</RenderOptions>
				)}
				{allowedCategories.includes("Restrict Applications") && (
					<RenderOptions
						optionsKey="Restrict Applications"
						useAccordion={isMobileLayout}
						expanded={expandedCategory === "Restrict Applications"}
						categoryTitle={getCategoryTitle("Restrict Applications")}
						handleExpandedChange={handleExpandedChange("Restrict Applications")}
					>
						<BlockItemsOptions
							option={option}
							updateOption={updateOption}
							disabled={disabled}
							shouldShowOption={shouldShowOption}
							isOptionAllowed={isOptionAllowed}
							applications={applications}
							horizontalOrientation={horizontalOrientation}
							getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
						/>
					</RenderOptions>
				)}
				{allowedCategories.includes("Other Activities") && (
					<RenderOptions
						optionsKey="Other Activities"
						useAccordion={isMobileLayout}
						expanded={expandedCategory === "Other Activities"}
						categoryTitle={getCategoryTitle("Other Activities")}
						handleExpandedChange={handleExpandedChange("Other Activities")}
					>
						<OtherActivitiesOptions
							option={option}
							updateOption={updateOption}
							disabled={disabled}
							shouldShowOption={shouldShowOption}
							isOptionAllowed={isOptionAllowed}
							horizontalOrientation={horizontalOrientation}
							getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
							courseId={courseId}
						/>
					</RenderOptions>
				)}
				{allowedCategories.includes("Show Tools") && (
					<RenderOptions
						optionsKey="Show Tools"
						useAccordion={isMobileLayout}
						expanded={expandedCategory === "Show Tools"}
						categoryTitle={getCategoryTitle("Show Tools")}
						handleExpandedChange={handleExpandedChange("Show Tools")}
					>
						<ShowToolsOptions
							option={option}
							updateOption={updateOption}
							disabled={disabled}
							shouldShowOption={shouldShowOption}
							isOptionAllowed={isOptionAllowed}
							horizontalOrientation={horizontalOrientation}
							getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
						/>
					</RenderOptions>
				)}
			</div>
		</div>
	);
}

export default ProctoringOptionsList;
